/**
 * Link safety for values that come from the API. A link is rendered only when it
 * is a plain http(s) URL, and always with rel="noopener noreferrer". Anything
 * else (javascript:, data:, vbscript:, protocol-relative, credentials in the
 * URL, control characters) yields null and the UI shows plain text instead.
 */

const HOSTNAME_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i

/** The normalised href when `value` is a safe http(s) URL, otherwise null. */
export function safeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  // eslint-disable-next-line no-control-regex
  if (!trimmed || /[\u0000-\u001f\u007f\s]/.test(trimmed)) return null
  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (url.username || url.password) return null
  return url.href
}

/** https:// link for a bare host name from the API, or null if it is not a host name. */
export function httpsUrlForHost(host: unknown): string | null {
  if (typeof host !== 'string') return null
  const trimmed = host.trim()
  if (!HOSTNAME_RE.test(trimmed)) return null
  return safeHttpUrl(`https://${trimmed}`)
}

/** Props to spread on an <a> for an external link. */
export function externalLinkProps(href: string) {
  return { href, target: '_blank', rel: 'noopener noreferrer' } as const
}
